import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

export default function OurWork() {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  const projects = [
    {
      title: "Digital Transformation",
      sector: "Technology",
      description: "Led comprehensive digital transformation for enterprise clients, modernizing legacy systems and implementing cloud solutions",
      year: "2024",
      icon: "💻",
    },
    {
      title: "Financial Restructuring",
      sector: "Finance",
      description: "Optimized capital structure and improved cash flow management for multiple financial institutions",
      year: "2024",
      icon: "💰",
    },
    {
      title: "Strategic Planning",
      sector: "Education",
      description: "Developed long-term strategic roadmaps for educational institutions to enhance operational efficiency",
      year: "2023",
      icon: "📚",
    },
    {
      title: "Market Expansion",
      sector: "Retail",
      description: "Facilitated successful market entry strategies across emerging markets with data-driven insights",
      year: "2023",
      icon: "🌍",
    },
    {
      title: "Healthcare Innovation",
      sector: "Healthcare",
      description: "Implemented cutting-edge healthcare technology solutions improving patient care and operations",
      year: "2025",
      icon: "🏥",
    },
    {
      title: "Supply Chain Optimization",
      sector: "Manufacturing",
      description: "Reduced operational costs by 25% through comprehensive supply chain redesign and automation",
      year: "2024",
      icon: "🏭",
    },
  ];

  const policyAdvisoryServices = {
    government: {
      title: "Policy Advisory Services to Government",
      subtitle: "Central Government, State Governments & Public Authorities",
      icon: "🏛️",
      sections: [
        {
          title: "Policy Formulation & Development Advisory",
          items: [
            "Advisory on formulation of new policies across sectors",
            "Policy visioning and long-term strategic frameworks",
            "Drafting of policy documents, white papers, and concept notes",
            "Alignment of policies with national priorities and development goals",
            "Inter-sectoral and cross-ministry policy coordination",
          ],
        },
        {
          title: "Policy Review, Reform & Rationalization",
          items: [
            "Review of existing policies and regulatory frameworks",
            "Identification of policy gaps, overlaps, and implementation challenges",
            "Policy reform and simplification advisory",
            "Regulatory impact assessment (RIA) and cost-benefit analysis",
            "Recommendations for ease of doing business and governance reforms",
          ],
        },
        {
          title: "Sector-Specific Policy Advisory",
          subtitle: "Across all Ministries and Departments",
          items: [
            "Infrastructure, urban and rural development policies",
            "Industrial, manufacturing, and investment policies",
            "Banking, finance, and capital market policies",
            "Agriculture, food security, and irrigation policies",
            "Energy, renewables, and climate policies",
            "Defence, railways, and transport policies",
            "Healthcare, education, and social sector policies",
            "Digital, IT, and e-governance policies",
          ],
        },
        {
          title: "Fiscal, Economic & Financial Policy Advisory",
          items: [
            "Public finance and budgetary policy advisory",
            "Subsidy rationalization and fiscal sustainability advisory",
            "Financial sector policy reforms and market development",
            "PPP and private investment policy frameworks",
            "Asset monetization and infrastructure financing policy advisory",
          ],
        },
        {
          title: "Federal, Inter-Governmental & Cooperative Federalism Advisory",
          items: [
            "Centre–State policy alignment advisory",
            "Inter-state coordination and cooperative federalism frameworks",
            "Policy advisory for federal fiscal and administrative matters",
            "Advisory on devolution, grants, and shared responsibilities",
          ],
        },
        {
          title: "Legal, Regulatory & Institutional Policy Advisory",
          items: [
            "Policy alignment with constitutional and legal frameworks",
            "Institutional capacity assessment and reform advisory",
            "Governance structure and institutional mechanism design",
            "Regulatory framework development and reform advisory",
          ],
        },
        {
          title: "Evidence-Based & Data-Driven Policy Advisory",
          items: [
            "Policy research and data analysis",
            "Use of economic modeling and scenario analysis",
            "Impact evaluation and outcome assessment",
            "International benchmarking and best-practice studies",
          ],
        },
        {
          title: "Policy Implementation & Monitoring Advisory",
          items: [
            "Translation of policy into programs and schemes",
            "Implementation roadmaps and action plans",
            "Monitoring & Evaluation (M&E) framework design",
            "Mid-course correction and policy recalibration advisory",
          ],
        },
        {
          title: "Stakeholder Consultation & Consensus Building",
          items: [
            "Design and facilitation of stakeholder consultations",
            "Industry, civil society, and expert engagement",
            "Public consultation and feedback analysis",
            "Consensus-building and communication strategy advisory",
          ],
        },
        {
          title: "Crisis, Reform & Special Assignment Advisory",
          items: [
            "Advisory during policy reforms and transitions",
            "Crisis-response policy advisory",
            "Rapid policy assessment and decision support",
            "Confidential advisory for sensitive policy matters",
          ],
        },
      ],
      coverage: "Policy advisory services extend across all Central and State Government ministries, departments, regulators, PSUs, and autonomous bodies.",
      disclaimer: "All policy advisory services are provided in accordance with applicable laws, constitutional provisions, and governmental processes. Final policy decisions rest solely with the competent government authorities.",
    },
    business: {
      title: "Policy Advocacy, Government Negotiation & Business Setup Advisory",
      subtitle: "Corporates, Businesses, HNIs & Family Offices",
      icon: "🏢",
      sections: [
        {
          title: "Policy Advocacy & Government Relations Advisory",
          subsections: [
            {
              title: "Central & State Government Policy Advisory",
              items: [
                "Advisory on Central and State Government industrial, sectoral, and investment policies",
                "Interpretation and impact assessment of existing and proposed policies",
                "Representation support and structured engagement with government authorities",
                "Advisory on policy reform suggestions and industry feedback mechanisms",
              ],
            },
            {
              title: "Government Negotiation & Facilitation",
              items: [
                "Strategic advisory for negotiations with Central and State Governments",
                "Preparation of negotiation strategy, briefing notes, and position papers",
                "Facilitation of meetings with ministries, departments, and state authorities",
                "Support in discussions related to incentives, approvals, and policy concessions",
              ],
            },
          ],
        },
        {
          title: "Industry-Specific Policy & Incentive Advisory",
          items: [
            "Advisory on state-specific industrial policies and sector incentives",
            "Identification of eligible subsidies, incentives, and benefits",
            "Structuring investment proposals aligned with government priorities",
            "Advisory on production-linked incentives (PLI), export incentives, and sector schemes",
            "Support for negotiations on land, infrastructure, utilities, and fiscal benefits",
          ],
        },
        {
          title: "Hassle-Free Business Setup & Entry Advisory",
          subtitle: "End-to-End State-Wise Support",
          subsections: [
            {
              title: "Location & State Selection Advisory",
              items: [
                "Comparative analysis of states based on policy, infrastructure, and incentives",
                "Advisory on sector-specific state advantages",
                "Site selection and industrial park / SEZ advisory",
              ],
            },
            {
              title: "Entity Setup & Regulatory Approvals",
              items: [
                "Advisory on optimal entity structure (company, LLP, branch, subsidiary)",
                "End-to-end support for registrations, licenses, and statutory approvals",
                "Single-window clearance facilitation advisory",
                "Coordination with state investment promotion agencies",
              ],
            },
          ],
        },
        {
          title: "Investment Promotion & Government Liaison",
          items: [
            "Advisory on engaging with State Investment Promotion Boards",
            "Assistance with Memorandums of Understanding (MoUs) with State Governments",
            "Support for investment proposals, project notes, and presentations",
            "Ongoing liaison with state and central authorities during project setup",
          ],
        },
        {
          title: "Land, Infrastructure & Utility Negotiation Advisory",
          items: [
            "Advisory and facilitation for land acquisition or allotment",
            "Negotiation support for industrial land, power, water, and logistics",
            "Infrastructure development and connectivity advisory",
            "Support for special dispensation and fast-track approvals",
          ],
        },
        {
          title: "Compliance, Risk & Policy Alignment Advisory",
          items: [
            "Regulatory mapping and compliance readiness assessment",
            "Advisory on environmental, labor, and local compliance requirements",
            "Risk assessment related to policy changes and regulatory exposure",
            "Dispute prevention and resolution strategy advisory",
          ],
        },
        {
          title: "Expansion, Relocation & Pan-India Growth Advisory",
          items: [
            "Advisory for business expansion across multiple states",
            "Relocation advisory for domestic and global businesses",
            "Support for branch, plant, and office setup across India",
            "Advisory on restructuring operations for policy and tax efficiency",
          ],
        },
        {
          title: "Advisory for HNIs & Family Offices",
          items: [
            "Investment structuring aligned with government policies",
            "Advisory on strategic investments requiring government engagement",
            "Support for large-scale and anchor investments",
            "Confidential advisory for sensitive government negotiations",
          ],
        },
      ],
      valueProposition: "We act as a strategic interface between businesses and governments, enabling policy-aligned investments, smooth negotiations, and hassle-free setup across Indian states.",
      disclaimer: "All advisory and facilitation services are provided in compliance with applicable laws and ethical standards. Final decisions, approvals, and policy outcomes rest with the respective government authorities.",
    },
  };

  const duplicatedProjects = [...projects, ...projects, ...projects];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || isPaused) return;

    const scrollSpeed = 1;
    let animationFrameId;

    const autoScroll = () => {
      if (scrollContainer) {
        scrollContainer.scrollLeft += scrollSpeed;
        const maxScroll = scrollContainer.scrollWidth / 3;
        if (scrollContainer.scrollLeft >= maxScroll) {
          scrollContainer.scrollLeft = 0;
        }
      }
      animationFrameId = requestAnimationFrame(autoScroll);
    };

    animationFrameId = requestAnimationFrame(autoScroll);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isPaused]);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleViewCaseStudies = () => {
    navigate('/contact');
  };

  return (
    <section className="py-12 md:py-20 bg-white text-black">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <h2
          className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 text-center text-black"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Our Work
        </h2>

        <p className="text-gray-700 text-sm md:text-base max-w-3xl mx-auto text-center mb-8 md:mb-12 px-4">
          We have advised startups, enterprises, and institutions across multiple sectors, 
          delivering measurable impact and long-term value.
        </p>

        {/* Projects Carousel */}
        <div 
          ref={scrollRef}
          className="flex space-x-4 md:space-x-6 overflow-x-auto mb-4 md:mb-8 pb-4 scrollbar-hide"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {duplicatedProjects.map((project, i) => (
            <div
              key={i}
              className="min-w-[260px] md:min-w-[300px] bg-white border-2 border-gray-200 hover:border-amber-500 p-5 md:p-6 rounded-xl flex-shrink-0 transition-all duration-300"
            >
              <div className="flex items-center mb-3">
                <div className="text-3xl">{project.icon}</div>
                <p className="ml-auto text-xs md:text-sm text-amber-600 font-semibold bg-amber-50 px-3 py-1 rounded-full">
                  {project.year}
                </p>
              </div>
              
              <h3 className="text-lg md:text-xl font-bold text-black mt-2 mb-1">
                {project.title}
              </h3>
              <p className="text-xs md:text-sm text-amber-600 font-medium mb-2">
                {project.sector}
              </p>
              <p className="text-xs md:text-sm text-gray-700 leading-relaxed">
                {project.description}
              </p>
            </div>
          ))}
        </div>

        <style jsx>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}</style>

        {/* Results Section */}
        <div className="mt-12 md:mt-16">
          <h3 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-center text-black">
            Results That Matter
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            <div className="bg-amber-50 border-2 border-amber-200 p-6 md:p-8 rounded-xl text-center hover:border-amber-500 transition-all duration-300">
              <p className="text-4xl md:text-5xl font-bold text-amber-600">35%</p>
              <p className="mt-2 text-sm md:text-base text-black font-medium">
                Improved Operational Efficiency
              </p>
            </div>
            <div className="bg-amber-50 border-2 border-amber-200 p-6 md:p-8 rounded-xl text-center hover:border-amber-500 transition-all duration-300">
              <p className="text-4xl md:text-5xl font-bold text-amber-600">28%</p>
              <p className="mt-2 text-sm md:text-base text-black font-medium">
                Reduced Costs & Risk
              </p>
            </div>
            <div className="bg-amber-50 border-2 border-amber-200 p-6 md:p-8 rounded-xl text-center hover:border-amber-500 transition-all duration-300">
              <p className="text-4xl md:text-5xl font-bold text-amber-600">50+</p>
              <p className="mt-2 text-sm md:text-base text-black font-medium">
                Digital Growth Projects
              </p>
            </div>
          </div>
        </div>

        {/* Policy Advisory Services Section */}
        <div className="mt-16 md:mt-24">
          <h3 className="text-2xl md:text-3xl font-bold mb-8 md:mb-12 text-center text-black">
            Comprehensive Policy Advisory Services
          </h3>

          {/* Government Services */}
          <div className="mb-8">
            <button
              onClick={() => toggleSection('government')}
              className="w-full bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-purple-300 hover:border-purple-500 p-6 rounded-xl transition-all duration-300 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <span className="text-4xl">{policyAdvisoryServices.government.icon}</span>
                <div className="text-left">
                  <h4 className="text-lg md:text-xl font-bold text-black">
                    {policyAdvisoryServices.government.title}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {policyAdvisoryServices.government.subtitle}
                  </p>
                </div>
              </div>
              <svg
                className={`w-6 h-6 transition-transform duration-300 ${
                  expandedSection === 'government' ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedSection === 'government' && (
              <div className="bg-white border-2 border-purple-200 rounded-b-xl p-6 mt-2">
                <div className="space-y-6">
                  {policyAdvisoryServices.government.sections.map((section, idx) => (
                    <div key={idx} className="border-l-4 border-purple-400 pl-4">
                      <h5 className="text-base md:text-lg font-bold text-black mb-2">
                        {section.title}
                      </h5>
                      {section.subtitle && (
                        <p className="text-sm text-purple-600 font-medium mb-2">
                          {section.subtitle}
                        </p>
                      )}
                      <ul className="space-y-2">
                        {section.items.map((item, itemIdx) => (
                          <li key={itemIdx} className="text-sm text-gray-700 flex items-start">
                            <span className="text-purple-500 mr-2 mt-1">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}

                  <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm font-semibold text-purple-900 mb-2">
                      Universal Policy Coverage
                    </p>
                    <p className="text-sm text-gray-700">
                      {policyAdvisoryServices.government.coverage}
                    </p>
                  </div>

                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-300">
                    <p className="text-xs text-gray-600 italic">
                      <span className="font-semibold">Disclaimer:</span> {policyAdvisoryServices.government.disclaimer}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Business Services */}
          <div className="mb-8">
            <button
              onClick={() => toggleSection('business')}
              className="w-full bg-gradient-to-r from-amber-50 to-amber-100 border-2 border-amber-300 hover:border-amber-500 p-6 rounded-xl transition-all duration-300 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <span className="text-4xl">{policyAdvisoryServices.business.icon}</span>
                <div className="text-left">
                  <h4 className="text-lg md:text-xl font-bold text-black">
                    {policyAdvisoryServices.business.title}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {policyAdvisoryServices.business.subtitle}
                  </p>
                </div>
              </div>
              <svg
                className={`w-6 h-6 transition-transform duration-300 ${
                  expandedSection === 'business' ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedSection === 'business' && (
              <div className="bg-white border-2 border-amber-200 rounded-b-xl p-6 mt-2">
                <div className="space-y-6">
                  {policyAdvisoryServices.business.sections.map((section, idx) => (
                    <div key={idx} className="border-l-4 border-amber-400 pl-4">
                      <h5 className="text-base md:text-lg font-bold text-black mb-2">
                        {section.title}
                      </h5>
                      {section.subtitle && (
                        <p className="text-sm text-amber-600 font-medium mb-2">
                          {section.subtitle}
                        </p>
                      )}
                      
                      {section.subsections ? (
                        <div className="space-y-4">
                          {section.subsections.map((subsection, subIdx) => (
                            <div key={subIdx} className="ml-4">
                              <p className="text-sm font-semibold text-gray-800 mb-2">
                                {subsection.title}
                              </p>
                              <ul className="space-y-2">
                                {subsection.items.map((item, itemIdx) => (
                                  <li key={itemIdx} className="text-sm text-gray-700 flex items-start">
                                    <span className="text-amber-500 mr-2 mt-1">•</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <ul className="space-y-2">
                          {section.items.map((item, itemIdx) => (
                            <li key={itemIdx} className="text-sm text-gray-700 flex items-start">
                              <span className="text-amber-500 mr-2 mt-1">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}

                  <div className="mt-6 p-4 bg-amber-50 rounded-lg">
                    <p className="text-sm font-semibold text-amber-900 mb-2">
                      Value Proposition
                    </p>
                    <p className="text-sm text-gray-700">
                      {policyAdvisoryServices.business.valueProposition}
                    </p>
                  </div>

                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-300">
                    <p className="text-xs text-gray-600 italic">
                      <span className="font-semibold">Disclaimer:</span> {policyAdvisoryServices.business.disclaimer}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CTA Button */}
        <div className="mt-12 md:mt-16 text-center">
          <button
            onClick={handleViewCaseStudies}
            className="bg-amber-600 hover:bg-amber-700 text-white font-semibold px-6 md:px-8 py-3 md:py-4 rounded-full transition-colors duration-300 shadow-lg hover:shadow-xl"
          >
            Get in Touch for Advisory Services
          </button>
        </div>
      </div>
    </section>
  );
}
